import React, { useState } from 'react';
import { TruckIcon, UserIcon, CheckIcon, XIcon, AlertTriangleIcon, CalendarIcon, ClockIcon, MapPinIcon, ArrowRightIcon, PhoneIcon, ClipboardIcon, InfoIcon } from 'lucide-react';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface Truck {
  id: string;
  truckNumber: string;
  type: string;
  make: string;
  model: string;
  year: number;
  status: string;
  assignedDriver?: string;
}
interface Driver {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: string;
  cdlExpiration: string;
  medicalCardExpiration: string;
}
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  date: string;
  startTime: string;
  material: string;
  truckType: string;
  loadLocation: string;
  dumpLocation: string;
  foreman: string;
  foremanPhone: string;
  estimatedHours: number;
  hourlyRate: number;
  source: 'broker' | 'contractor';
  sourceName: string;
  priority: 'low' | 'medium' | 'high';
  status: 'pending' | 'assigned' | 'in-progress' | 'completed';
  assignedTruck?: string;
  assignedDriver?: string;
  comments?: string;
  geoPunchRequired?: boolean;
  geoPunchDistance?: number;
}
interface JobAssignmentsProps {
  jobs: Job[];
  trucks: Truck[];
  drivers: Driver[];
}
export const JobAssignments = ({
  jobs,
  trucks,
  drivers
}: JobAssignmentsProps) => {
  const [assignments, setAssignments] = useState<Record<string, {
    truckId: string;
    driverId: string;
  }>>({});
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  // Filter trucks that match the job's truck type requirement and are available
  const getMatchingTrucks = (job: Job) => {
    return trucks.filter(truck => truck.type.toLowerCase() === job.truckType.toLowerCase() && truck.status === 'available');
  };
  // Filter active drivers
  const getAvailableDrivers = () => {
    return drivers.filter(driver => driver.status === 'active');
  };
  // Check if driver has valid documents
  const hasValidDocuments = (driver: Driver) => {
    const now = new Date();
    return new Date(driver.cdlExpiration) > now && new Date(driver.medicalCardExpiration) > now;
  };
  const handleAssign = (job: Job, truckId: string, driverId: string) => {
    setAssignments({
      ...assignments,
      [job.id]: {
        truckId,
        driverId
      }
    });
  };
  const handleConfirmAssignment = (job: Job) => {
    setSelectedJob(job);
    setShowConfirmation(true);
  };
  const confirmAssignment = () => {
    if (!selectedJob) return;
    // In a real app, this would send the assignment to your backend
    console.log(`Job ${selectedJob.id} assigned to truck ${assignments[selectedJob.id]?.truckId} with driver ${assignments[selectedJob.id]?.driverId}`);
    setShowConfirmation(false);
    setSelectedJob(null);
    // Show success message or update job status
    alert(`Job "${selectedJob.jobName}" successfully assigned!`);
  };
  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'high':
        return <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium" data-id="element-3747">
            High Priority
          </span>;
      case 'medium':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-medium" data-id="element-3748">
            Medium Priority
          </span>;
      default:
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium" data-id="element-3749">
            Low Priority
          </span>;
    }
  };
  return <div className="bg-white rounded-lg shadow" data-id="element-3750">
      <div className="p-6 border-b" data-id="element-3751">
        <h2 className="text-xl font-bold" data-id="element-3752">Job Assignments</h2>
        <p className="text-gray-600 mt-1" data-id="element-3753">
          Assign trucks and drivers to pending jobs
        </p>
      </div>

      {jobs.length === 0 ? <div className="p-6 text-center" data-id="element-3754">
          <ClipboardIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-3755" />
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-3756">
            No pending jobs
          </h3>
          <p className="text-gray-600" data-id="element-3757">
            There are currently no jobs waiting to be assigned
          </p>
        </div> : <div className="divide-y" data-id="element-3758">
          {jobs.map(job => {
        const isExpanded = expandedJob === job.id;
        const matchingTrucks = getMatchingTrucks(job);
        const availableDrivers = getAvailableDrivers();
        const hasAssignment = assignments[job.id];
        const jobDate = new Date(job.date);
        const isToday = new Date().toDateString() === jobDate.toDateString();
        const isTomorrow = new Date(new Date().setDate(new Date().getDate() + 1)).toDateString() === jobDate.toDateString();
        return <div key={job.id} className="p-4 hover:bg-gray-50" data-id="element-3759">
                <div className="flex justify-between items-start" data-id="element-3760">
                  <div className="flex-1" data-id="element-3761">
                    <div className="flex items-center gap-2 mb-1" data-id="element-3762">
                      <h3 className="font-medium" data-id="element-3763">{job.jobName}</h3>
                      {getPriorityBadge(job.priority)}
                      {isToday && <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium" data-id="element-3764">
                          Today
                        </span>}
                      {isTomorrow && <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs font-medium" data-id="element-3765">
                          Tomorrow
                        </span>}
                    </div>
                    <div className="text-sm text-gray-500 mb-2" data-id="element-3766">
                      Job #{job.jobNumber} • {job.material} • {job.truckType}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3" data-id="element-3767">
                      <div className="flex items-center gap-1 text-sm" data-id="element-3768">
                        <CalendarIcon size={14} className="text-gray-400" data-id="element-3769" />
                        <span data-id="element-3770">{new Date(job.date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm" data-id="element-3771">
                        <ClockIcon size={14} className="text-gray-400" data-id="element-3772" />
                        <span data-id="element-3773">
                          {job.startTime} • ~{job.estimatedHours} hrs
                        </span>
                      </div>
                      <div className="flex items-center gap-1 text-sm" data-id="element-3774">
                        <UserIcon size={14} className="text-gray-400" data-id="element-3775" />
                        <span data-id="element-3776">Foreman: {job.foreman}</span>
                      </div>
                    </div>
                    {hasAssignment && <div className="bg-green-50 border border-green-200 rounded-md p-2 mb-3" data-id="element-3777">
                        <div className="flex items-center gap-2 text-sm text-green-800" data-id="element-3778">
                          <CheckIcon size={14} className="text-green-600" data-id="element-3779" />
                          <span className="font-medium" data-id="element-3780">
                            Assigned to{' '}
                            {trucks.find(t => t.id === hasAssignment.truckId)?.truckNumber}{' '}
                            with driver{' '}
                            {drivers.find(d => d.id === hasAssignment.driverId)?.name}
                          </span>
                        </div>
                      </div>}
                  </div>
                  <div className="flex items-center gap-2" data-id="element-3781">
                    <button onClick={() => setExpandedJob(isExpanded ? null : job.id)} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100" data-id="element-3782">
                      {isExpanded ? 'Hide Details' : 'Assign'}
                    </button>
                    {hasAssignment && <button onClick={() => handleConfirmAssignment(job)} className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700" data-id="element-3783">
                        Confirm
                      </button>}
                  </div>
                </div>

                {isExpanded && <div className="mt-4 border-t pt-4" data-id="element-3784">
                    <div className="mb-4" data-id="element-3785">
                      <h4 className="font-medium text-sm mb-2" data-id="element-3786">
                        Job Communication
                      </h4>
                      <div className="flex flex-wrap gap-2" data-id="element-3787">
                        <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="foreman" buttonText="Message Foreman" className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1" data-id="element-3788" />
                        {job.source === 'broker' && <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="broker" buttonText="Message Broker" className="px-3 py-1 text-sm bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center gap-1" data-id="element-3789" />}
                        {job.source === 'contractor' && <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="contractor" buttonText="Message Contractor" className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-1" data-id="element-3790" />}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-3791">
                      <div data-id="element-3792">
                        <h4 className="font-medium text-sm mb-2" data-id="element-3793">
                          Select Truck ({job.truckType})
                        </h4>
                        {matchingTrucks.length > 0 ? <div className="border rounded-md divide-y max-h-48 overflow-y-auto" data-id="element-3794">
                            {matchingTrucks.map(truck => <div key={truck.id} className={`p-2 flex items-center justify-between cursor-pointer hover:bg-blue-50 ${assignments[job.id]?.truckId === truck.id ? 'bg-blue-50' : ''}`} onClick={() => {
                    // If no driver selected yet, use the first available driver
                    const driverId = assignments[job.id]?.driverId || (availableDrivers.length > 0 ? availableDrivers[0].id : '');
                    handleAssign(job, truck.id, driverId);
                  }} data-id="element-3795">
                                <div className="flex items-center gap-2" data-id="element-3796">
                                  <TruckIcon size={16} className="text-gray-500" data-id="element-3797" />
                                  <div data-id="element-3798">
                                    <div className="font-medium text-sm" data-id="element-3799">
                                      {truck.truckNumber}
                                    </div>
                                    <div className="text-xs text-gray-500" data-id="element-3800">
                                      {truck.make} {truck.model} ({truck.year})
                                    </div>
                                  </div>
                                </div>
                                {assignments[job.id]?.truckId === truck.id && <CheckIcon size={16} className="text-blue-600" data-id="element-3801" />}
                              </div>)}
                          </div> : <div className="border rounded-md p-3 bg-yellow-50 text-yellow-800 text-sm" data-id="element-3802">
                            <div className="flex items-center gap-2" data-id="element-3803">
                              <AlertTriangleIcon size={16} data-id="element-3804" />
                              <span data-id="element-3805">
                                No matching trucks available for this job type
                              </span>
                            </div>
                          </div>}
                      </div>

                      <div data-id="element-3806">
                        <h4 className="font-medium text-sm mb-2" data-id="element-3807">
                          Select Driver
                        </h4>
                        {availableDrivers.length > 0 ? <div className="border rounded-md divide-y max-h-48 overflow-y-auto" data-id="element-3808">
                            {availableDrivers.map(driver => {
                    const isValid = hasValidDocuments(driver);
                    return <div key={driver.id} className={`p-2 flex items-center justify-between ${!isValid ? 'bg-red-50 cursor-not-allowed' : 'cursor-pointer hover:bg-blue-50'} ${assignments[job.id]?.driverId === driver.id ? 'bg-blue-50' : ''}`} onClick={() => {
                      if (!isValid) return;
                      // If no truck selected yet, use the first matching truck
                      const truckId = assignments[job.id]?.truckId || (matchingTrucks.length > 0 ? matchingTrucks[0].id : '');
                      handleAssign(job, truckId, driver.id);
                    }} data-id="element-3809">
                                  <div className="flex items-center gap-2" data-id="element-3810">
                                    <UserIcon size={16} className="text-gray-500" data-id="element-3811" />
                                    <div data-id="element-3812">
                                      <div className="font-medium text-sm" data-id="element-3813">
                                        {driver.name}
                                      </div>
                                      <div className="text-xs text-gray-500" data-id="element-3814">
                                        {driver.phone}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center" data-id="element-3815">
                                    {!isValid && <span className="text-xs text-red-600 mr-2" data-id="element-3816">
                                        Invalid docs
                                      </span>}
                                    {assignments[job.id]?.driverId === driver.id && <CheckIcon size={16} className="text-blue-600" data-id="element-3817" />}
                                  </div>
                                </div>;
                  })}
                          </div> : <div className="border rounded-md p-3 bg-yellow-50 text-yellow-800 text-sm" data-id="element-3818">
                            <div className="flex items-center gap-2" data-id="element-3819">
                              <AlertTriangleIcon size={16} data-id="element-3820" />
                              <span data-id="element-3821">No available drivers</span>
                            </div>
                          </div>}
                      </div>
                    </div>

                    {job.geoPunchRequired && <div className="mb-4" data-id="element-3822">
                        <h4 className="font-medium text-sm mb-2" data-id="element-3823">
                          Geo Punch Requirements
                        </h4>
                        <div className="bg-blue-50 p-3 rounded-md" data-id="element-3824">
                          <div className="flex items-start gap-2" data-id="element-3825">
                            <MapPinIcon size={16} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-3826" />
                            <div data-id="element-3827">
                              <div className="text-sm font-medium" data-id="element-3828">
                                Location Verification Required
                              </div>
                              <div className="text-sm text-gray-600" data-id="element-3829">
                                Drivers must be within{' '}
                                {job.geoPunchDistance || 150} feet of job
                                locations to clock in/out
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>}

                    <div className="flex justify-between items-center" data-id="element-3830">
                      <div className="text-sm text-gray-500 flex items-center gap-1" data-id="element-3831">
                        <InfoIcon size={14} data-id="element-3832" />
                        <span data-id="element-3833">
                          From:{' '}
                          {job.source === 'broker' ? 'Broker' : 'Contractor'} -{' '}
                          {job.sourceName}
                        </span>
                      </div>
                      <div className="flex gap-2" data-id="element-3834">
                        <button onClick={() => setExpandedJob(null)} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100" data-id="element-3835">
                          Cancel
                        </button>
                        {hasAssignment && <button onClick={() => handleConfirmAssignment(job)} className="px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700" data-id="element-3836">
                            Confirm Assignment
                          </button>}
                      </div>
                    </div>
                  </div>}
              </div>;
      })}
        </div>}

      {/* Confirmation Modal */}
      {showConfirmation && selectedJob && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-3837">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6" data-id="element-3838">
            <h3 className="text-lg font-semibold mb-4" data-id="element-3839">
              Confirm Job Assignment
            </h3>
            <div className="mb-4" data-id="element-3840">
              <p className="text-gray-700 mb-2" data-id="element-3841">
                You are about to assign the following job:
              </p>
              <div className="bg-gray-50 p-3 rounded-md" data-id="element-3842">
                <p className="font-medium" data-id="element-3843">{selectedJob.jobName}</p>
                <p className="text-sm text-gray-600" data-id="element-3844">
                  Job #{selectedJob.jobNumber}
                </p>
                <p className="text-sm text-gray-600" data-id="element-3845">
                  {new Date(selectedJob.date).toLocaleDateString()} at{' '}
                  {selectedJob.startTime}
                </p>
              </div>
            </div>
            {assignments[selectedJob.id] && <div className="mb-6" data-id="element-3846">
                <div className="grid grid-cols-2 gap-4" data-id="element-3847">
                  <div className="bg-blue-50 p-3 rounded-md" data-id="element-3848">
                    <h4 className="text-sm font-medium text-blue-800 mb-1" data-id="element-3849">
                      Assigned Truck
                    </h4>
                    <p className="text-sm" data-id="element-3850">
                      {trucks.find(t => t.id === assignments[selectedJob.id].truckId)?.truckNumber}{' '}
                      (
                      {trucks.find(t => t.id === assignments[selectedJob.id].truckId)?.type}
                      )
                    </p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-md" data-id="element-3851">
                    <h4 className="text-sm font-medium text-blue-800 mb-1" data-id="element-3852">
                      Assigned Driver
                    </h4>
                    <p className="text-sm" data-id="element-3853">
                      {drivers.find(d => d.id === assignments[selectedJob.id].driverId)?.name}
                    </p>
                  </div>
                </div>
              </div>}
            <div className="flex items-center justify-between border-t pt-4" data-id="element-3854">
              <button onClick={() => {
            setShowConfirmation(false);
            setSelectedJob(null);
          }} className="px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50" data-id="element-3855">
                Cancel
              </button>
              <button onClick={confirmAssignment} className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700" data-id="element-3856">
                Confirm Assignment
              </button>
            </div>
          </div>
        </div>}
    </div>;
};